import { Container, LoadingScreen } from '@/shared/components'
import MissingResourcesAlert from '@/shared/components/MissingResourcesAlert'
import SupplierEditDialog from './dialogs/SupplierEditDialog'
import { useGetAllInstallationsForPartnership } from '@/features/suppliers/hooks/installations'
import { useGetAllPartnerships } from '@/features/suppliers/hooks/partnerships'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import {
  BreadcrumbsBar,
  Button,
  DataBlock,
  H2,
  Table,
  TableColumnHeader,
  TableRow,
  PageSection
} from '@/common'
import useDialog from '@/common/hooks/useDialog'
import { compact } from 'es-toolkit'
import { useMemo } from 'react'
import { Navigate, useParams, Routes, Route, useNavigate } from 'react-router-dom'
import InstallationCreateDialog from './dialogs/InstallationCreateDialog'
import InstallationDetailsPage from './pages/installation-details'
import InstallationRow from './pages/installation-details/components/InstallationRow'
import { useGetAllImportedGoods } from '@/features/cbam-reporting/hooks/importedGoods'
import DeletePartnershipDialog from '../../dialogs/DeletePartnershipDialog'
import DeleteInstallationDialog from './dialogs/DeleteInstallationDialog'
import { Installation } from '@/features/suppliers/hooks/installations'
import { useState } from 'react'

export default function SupplierDetails() {
  return (
    <Routes>
      <Route path="installations/:installationId" element={<InstallationDetailsPage />} />
      <Route path="/" element={<SupplierDetailsPage />} />
    </Routes>
  )
}

function SupplierDetailsPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const {
    open: isEditSupplierDialogOpen,
    openDialog: openEditSupplierDialog,
    closeDialog: closeEditSupplierDialog
  } = useDialog()
  const {
    open: isCreateInstallationDialogOpen,
    openDialog: openCreateInstallationDialog,
    closeDialog: closeCreateInstallationDialog
  } = useDialog()
  const {
    open: isDeletePartnershipDialogOpen,
    openDialog: openDeletePartnershipDialog,
    closeDialog: closeDeletePartnershipDialog
  } = useDialog()
  const {
    open: isDeleteInstallationDialogOpen,
    openDialog: openDeleteInstallationDialog,
    closeDialog: closeDeleteInstallationDialog
  } = useDialog()
  const [selectedInstallation, setSelectedInstallation] = useState<Installation | null>(null)

  const organizationId = useSession().session.organization.id
  const partnershipId = useParams().partnershipId!

  const partnershipsQuery = useGetAllPartnerships(organizationId)
  const installationsQuery = useGetAllInstallationsForPartnership(partnershipId)
  const importedGoodsQuery = useGetAllImportedGoods(organizationId)

  const arePartnershipsLoading = partnershipsQuery.isLoading
  const areInstallationsLoading = installationsQuery.isLoading
  const areImportedGoodsLoading = importedGoodsQuery.isLoading

  const partnerships = partnershipsQuery.data ?? []
  const installations = installationsQuery.data ?? []
  const importedGoods = importedGoodsQuery.data ?? []

  const partnership = partnerships.find(partnership => partnership.id === partnershipId)
  const hasInstallations = installations.length > 0
  const importedGoodsForPartnership = importedGoods.filter(
    importedGood => importedGood.partnershipId === partnershipId
  )

  const partnershipContactPerson = useMemo(
    () =>
      compact([
        partnership?.operatorContactName,
        partnership?.operatorContactPhoneNumber,
        partnership?.operatorContactEmail
      ]).join(', '),
    [partnership]
  )

  const partnershipAddress = useMemo(() => {
    const streetAndNumber =
      partnership?.operatorStreet || partnership?.operatorStreetNumber
        ? compact([partnership?.operatorStreet, partnership?.operatorStreetNumber]).join(' ')
        : ''

    const address = compact([
      streetAndNumber,
      partnership?.operatorPostCode,
      partnership?.operatorCity,
      t(`common:countriesByIsoAlpha2.${partnership?.country}`)
    ]).join(', ')
    return address
  }, [partnership, t])

  if (arePartnershipsLoading || areInstallationsLoading || areImportedGoodsLoading) {
    return <LoadingScreen />
  }

  if (!partnership || !partnershipId) {
    return <Navigate to="/suppliers" />
  }

  const breadcrumbs = [
    { label: t('common:breadcrumbs.suppliers'), to: '/suppliers' },
    { label: partnership?.name || '', to: '' }
  ]
  return (
    <>
      <BreadcrumbsBar links={breadcrumbs} />
      <Container>
        <header className="flex justify-between items-center">
          <H2>{partnership?.name}</H2>
        </header>
        <PageSection
          title={t('suppliers:supplierViewPage.supplierDetails.title')}
          actions={
            <div className="flex gap-2">
              <Button variant="txt-default" onClick={openEditSupplierDialog}>
                {t('suppliers:supplierViewPage.supplierDetails.actions.edit')}
              </Button>
              <Button variant="danger" onClick={openDeletePartnershipDialog}>
                {t('common:actions.delete')}
              </Button>
            </div>
          }
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <DataBlock
              label={t('suppliers:supplierViewPage.supplierDetails.dataSections.contactPerson')}
              data={partnershipContactPerson}
            />
            <DataBlock
              label={t('suppliers:supplierViewPage.supplierDetails.dataSections.address')}
              data={partnershipAddress}
            />
          </div>
        </PageSection>
        <PageSection
          title={t('suppliers:supplierViewPage.installations.title')}
          actions={
            hasInstallations && (
              <Button
                onClick={() => {
                  openCreateInstallationDialog()
                }}
              >
                {t('suppliers:supplierViewPage.installations.actions.add')}
              </Button>
            )
          }
        >
          {!hasInstallations ? (
            <MissingResourcesAlert
              title={t('suppliers:supplierViewPage.installations.missingInstallations.title')}
              description={t(
                'suppliers:supplierViewPage.installations.missingInstallations.description'
              )}
              action={{
                label: t('suppliers:supplierViewPage.installations.missingInstallations.action'),
                onClick: () => {
                  openCreateInstallationDialog()
                }
              }}
            />
          ) : (
            <Table>
              <thead>
                <TableRow>
                  <TableColumnHeader>
                    {t('suppliers:supplierViewPage.installations.table.headers.installationName')}
                  </TableColumnHeader>
                  <TableColumnHeader>
                    {t('suppliers:supplierViewPage.installations.table.headers.cityCountry')}
                  </TableColumnHeader>
                  <TableColumnHeader>
                    {t('suppliers:supplierViewPage.installations.table.headers.economicActivity')}
                  </TableColumnHeader>
                  <TableColumnHeader align="right">
                    {t(
                      'suppliers:supplierViewPage.installations.table.headers.supplierEmissionsReports'
                    )}
                  </TableColumnHeader>
                  <TableColumnHeader align="right">
                    {t('suppliers:supplierViewPage.installations.table.headers.quantityReported')}
                  </TableColumnHeader>
                  <TableColumnHeader />
                </TableRow>
              </thead>
              <tbody>
                {installations.map(installation => (
                  <InstallationRow
                    key={installation.id}
                    installation={installation}
                    importedGoods={importedGoodsForPartnership.filter(
                      importedGood => importedGood.installationId === installation.id
                    )}
                    onDeleteClick={() => {
                      setSelectedInstallation(installation)
                      openDeleteInstallationDialog()
                    }}
                  />
                ))}
              </tbody>
            </Table>
          )}
        </PageSection>
      </Container>
      <SupplierEditDialog
        isOpen={isEditSupplierDialogOpen}
        close={closeEditSupplierDialog}
        partnership={partnership || null}
        organizationId={organizationId}
      />
      <InstallationCreateDialog
        isOpen={isCreateInstallationDialogOpen}
        close={closeCreateInstallationDialog}
        partnershipId={partnershipId}
        supplierCountry={partnership.country}
        partnership={partnership}
      />
      <DeletePartnershipDialog
        isOpen={isDeletePartnershipDialogOpen}
        close={closeDeletePartnershipDialog}
        partnershipName={partnership.name}
        partnershipId={partnershipId}
        onAfterLeave={() => navigate('/suppliers')}
      />
      {selectedInstallation && (
        <DeleteInstallationDialog
          isOpen={isDeleteInstallationDialogOpen}
          close={closeDeleteInstallationDialog}
          installationName={selectedInstallation.name}
          partnershipId={partnershipId}
          installationId={selectedInstallation.id}
        />
      )}
    </>
  )
}
